import * as React from "react";

import { AgentThreadSidebar } from "@/AgentThread/index";
import { BotMessageSquare } from "@/shared/foundation/icons";
import {
  CollaborationBarStorage,
  useCollaborationBar,
  type CollaborationBarLocation,
  type CollaborationMode,
} from "@/shared/state/collaborationBar";

import styles from "./CollaborationBar.module.css";
import { ModeButton } from "./ModeButton";
import { ResizeHandle } from "./ResizeHandle";

interface CollaborationBarContentProps {
  location: CollaborationBarLocation;
  onNavigate: (location: CollaborationBarLocation) => void;
  onClose: () => void;
}

const CollaborationBarContent = ({
  location,
  onNavigate,
  onClose,
}: CollaborationBarContentProps) => {
  switch (location.mode) {
    case "ai-agent-chat":
      return (
        <div className={styles.contentPlaceholder}>
          <AgentThreadSidebar
            location={location}
            onNavigate={onNavigate}
            onClose={onClose}
          />
        </div>
      );
    default:
      return null;
  }
};

/**
 * CollaborationBar component - a right-side sidebar for AI threads and collaboration features.
 *
 * Collapsed state: 40px wide with mode buttons (AI Chat, Comments)
 * Expanded state: Resizable width with mode-specific content area
 */
export function CollaborationBar() {
  const collaborationBar = useCollaborationBar();
  const contentRef = React.useRef<HTMLDivElement>(null);

  const onResize = React.useCallback((deltaX: number) => {
    if (!contentRef.current) {
      return;
    }

    const currentWidth = contentRef.current.offsetWidth;
    const newWidth = currentWidth + deltaX;

    contentRef.current.style.width = `${newWidth}px`;
    CollaborationBarStorage.setWidth(newWidth);
  }, []);

  // Initialize width from localStorage when expanded
  React.useEffect(
    function initializeWidthFromLocalStorage() {
      if (!contentRef.current || !collaborationBar.isOpen) {
        return;
      }

      const savedWidth = CollaborationBarStorage.getWidth();
      if (savedWidth !== null) {
        // The value is gated by minWidth and maxWidth in CSS
        contentRef.current.style.width = `${savedWidth}px`;
      }
    },
    [collaborationBar.isOpen],
  );

  const handleModeClick = React.useCallback(
    (mode: CollaborationMode) => {
      if (collaborationBar.isOpen && collaborationBar.location.mode === mode) {
        collaborationBar.close();
        return;
      }

      // Bare `open()` lands on the remembered address. There is one mode
      // today, so that address always belongs to the mode being clicked; a
      // second mode turns this into a navigate-to-that-mode's-default call.
      collaborationBar.open();
    },
    [collaborationBar],
  );

  return (
    <div className={styles.collaborationBar}>
      {/* Collapsed bar with mode buttons - always visible */}
      <div className={styles.collapsedBar}>
        <ModeButton
          isActive={
            collaborationBar.isOpen &&
            collaborationBar.location.mode === "ai-agent-chat"
          }
          onClick={() => handleModeClick("ai-agent-chat")}
        >
          <div className={styles.textAndIcon}>
            <BotMessageSquare size={20} />
            AI Chat
          </div>
        </ModeButton>
      </div>

      {/* Expanded content - only visible when open */}
      {collaborationBar.isOpen && (
        <div ref={contentRef} className={styles.expandedContent}>
          <ResizeHandle onResize={onResize} />
          <CollaborationBarContent
            location={collaborationBar.location}
            onNavigate={collaborationBar.open}
            onClose={collaborationBar.close}
          />
        </div>
      )}
    </div>
  );
}
